import type { ProjectResource, TagSetResource } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { useRef, useState } from "react";
import { repository } from "~/clientInstance";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import { useDoBusyTaskEffect } from "~/components/DataBaseComponent";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import type { RunbooksPageQueryParams, FilterableRunbookCollection } from "../RunbooksPage";
import type { RunbooksFilterState } from "./useRunbooksFilterState";
import { useRunbooksFilterState } from "./useRunbooksFilterState";
interface RunbooksStateActions {
    setPageSize: (newPageSize: number) => void;
    setRunbooksFilter: (newFilter: RunbooksFilterState) => void;
}
interface RunbooksState {
    runbooks: FilterableRunbookCollection | undefined;
    tagSets: TagSetResource[];
    filterState: RunbooksFilterState;
    take: number;
    pageSize: number;
    runbooksActions: RunbooksStateActions;
    reload: () => Promise<boolean>;
}
const DEFAULT_PAGE_SIZE = 30;
export function useRunbooksState(doBusyTask: DoBusyTask, queryParams: RunbooksPageQueryParams, setQueryParams: QueryParamValuesSetter<RunbooksPageQueryParams>, project: ProjectResource, gitRef: string | undefined, hasGitRunbooks: boolean, onFirstLoad?: () => void): RunbooksState {
    const [runbooks, setRunbooks] = useState<FilterableRunbookCollection | undefined>();
    const [tagSets, setTagSets] = useState<TagSetResource[]>([]);
    const [take, setTake] = useState<number>(DEFAULT_PAGE_SIZE);
    const [localFilterByName, setLocalFilterByName] = useState<string | undefined>();
    const [filterState, setFilterState] = useRunbooksFilterState(queryParams, setQueryParams, localFilterByName);
    const firstLoad = useRef(true);
    const runbookTagsKey = queryParams.runbookTags?.join(",") || "";
    const reload = useDoBusyTaskEffect(doBusyTask, async () => {
        // Capture filter values locally at the start in case they change between when the search
        // starts and when we get the results below, so we can work out if there was a filter
        // when we made the request.
        const params = { skip: 0, take, partialName: filterState.filterByName, runbookTags: queryParams.runbookTags?.join(",") };
        const isFiltered = !!(filterState.filterByName || (queryParams.runbookTags && queryParams.runbookTags.length > 0));
        if (hasGitRunbooks) {
            if (gitRef) {
                const runbooksData = await repository.Runbooks.getRunbooks(project, gitRef, params);
                setRunbooks({ ...runbooksData, IsFiltered: isFiltered });
            }
        }
        else {
            const runbooksData = await repository.Runbooks.getRunbooks(project, undefined, params);
            setRunbooks({ ...runbooksData, IsFiltered: isFiltered });
        }
        const runbookTagSets = await repository.TagSets.getAllByScopes(["Runbook"]);
        setTagSets(runbookTagSets);
        if (firstLoad.current && onFirstLoad) {
            firstLoad.current = false;
            onFirstLoad();
        }
    }, [gitRef, take, filterState.filterByName, runbookTagsKey, hasGitRunbooks, project, queryParams.runbookTags], { timeOperationOptions: timeOperationOptions.forInitialLoad(project.IsVersionControlled) });
    const actions: RunbooksStateActions = {
        setPageSize: (newPageSize) => setTake((currentTake) => currentTake + newPageSize),
        setRunbooksFilter: (newFilter) => {
            setLocalFilterByName(newFilter.filterByName);
            setFilterState(newFilter);
            setTake(DEFAULT_PAGE_SIZE); // Reset pagination when filters change
        },
    };
    return {
        runbooks,
        tagSets,
        filterState,
        take,
        pageSize: DEFAULT_PAGE_SIZE,
        runbooksActions: actions,
        reload,
    };
}
